<!-- Extend across the CMS horizontal padding; keep content inset inside each region. -->
<div class="-mx-16 flex h-full min-h-0 flex-col">
  @if (isLoading()) {
    <div class="px-21 pt-32">
      <aw-loading-animation />
    </div>
  } @else {
    @let hasChanges = hasConfigurationChanges(configurationForm);

    <div
      class="flex shrink-0 flex-col gap-4 overflow-y-auto bg-white px-21 pt-5 pb-4 [scrollbar-gutter:stable]">
      <aw-section-header>
        <aw-section-headline>
          {{ licenseLabel() || cmsTextProvider.configuration.configuration }}
        </aw-section-headline>
        <aw-section-header-actions>
          <div class="flex gap-2">
            <aw-button
              id="license-configuration-cancel"
              type="button"
              [style]="'primary-alt'"
              [isDisabled]="!hasChanges"
              [text]="cmsTextProvider.shared.cancel"
              (click)="handleAbortChangesDialog()" />
            <aw-button
              id="license-configuration-save"
              type="button"
              [style]="'primary'"
              [isDisabled]="!hasChanges"
              [text]="cmsTextProvider.shared.save"
              (click)="save()" />
          </div>
        </aw-section-header-actions>
      </aw-section-header>

      <div class="flex flex-wrap items-center justify-between gap-4">
        <div class="flex flex-wrap items-center gap-4">
          <aw-search-bar
            class="w-64"
            [formControl]="searchControl"
            [placeholder]="cmsTextProvider.shared.search" />

          @if (!hasSingleActiveLicenseChannel()) {
            <span class="text-base font-semibold">{{
              cmsTextProvider.configuration.showProducts
            }}</span>
            <button
              type="button"
              [attr.aria-pressed]="showWeb()"
              (click)="toggleProductChannel(configurationChannel.Web)">
              <aw-toggle-chip
                [text]="cmsTextProvider.license.web"
                [isChecked]="showWeb()" />
            </button>
            <button
              type="button"
              [attr.aria-pressed]="showApp()"
              (click)="toggleProductChannel(configurationChannel.App)">
              <aw-toggle-chip
                [text]="cmsTextProvider.license.app"
                [isChecked]="showApp()" />
            </button>
          }
        </div>

        <button
          type="button"
          class="text-primary-main hover:bg-standard-hover-main flex cursor-pointer items-center gap-2 rounded-md px-2 py-1 font-semibold transition-colors"
          (click)="toggleAllGroups()">
          <span>
            {{
              areAllGroupsCollapsed()
                ? cmsTextProvider.configuration.expandAll
                : cmsTextProvider.configuration.collapseAll
            }}
          </span>
          <span class="material-symbols-rounded text-xl!">
            {{ areAllGroupsCollapsed() ? 'unfold_more' : 'unfold_less' }}
          </span>
        </button>
      </div>

      @if (configurationGroups().length > 0) {
        <div
          class="border-standard-outline-light flex border-b text-base font-semibold"
          data-testid="configuration-table-header">
          <ng-container *ngTemplateOutlet="tableHeader" />
        </div>
      }
    </div>

    <div
      [formGroup]="configurationForm"
      class="flex min-h-0 w-full flex-1 flex-col overflow-y-auto px-21 pb-5 [scrollbar-gutter:stable]">
      @for (group of configurationGroups(); track group.id) {
        @if (group.name) {
          <aw-section
            class="block w-full"
            [class.hidden]="!configurationGroupMatchesSearch(group)"
            [id]="group.id"
            [testIdAffix]="'license-configuration-' + group.id"
            [isCollapsible]="true"
            [showDividerWhenCollapsed]="true"
            [withoutContentPadding]="true"
            [isInitiallyCollapsed]="false">
            <aw-section-header class="px-3 py-4">
              <aw-section-headline size="small">
                <span #headlineText class="inline-flex items-center gap-2">
                  {{ group.name }}
                  @if (group.description) {
                    <aw-tooltip
                      class="inline-flex items-center self-center"
                      [text]="group.description" />
                  }
                </span>
              </aw-section-headline>
            </aw-section-header>

            <aw-simple-table
              class="block w-full"
              [id]="'configuration-' + group.id"
              [data]="group.rows"
              [applyDefaultCellStyles]="false"
              [showHeader]="false"
              [showLastRowBorder]="true"
              [trackBy]="configurationRowKey"
              [isRowVisible]="configurationRowMatchesSearch"
              [header]="tableHeader"
              [columns]="tableColumns" />
          </aw-section>
        } @else {
          <aw-simple-table
            class="block w-full"
            [class.hidden]="!configurationGroupMatchesSearch(group)"
            [id]="'configuration-' + group.id"
            [data]="group.rows"
            [applyDefaultCellStyles]="false"
            [showHeader]="false"
            [showLastRowBorder]="true"
            [trackBy]="configurationRowKey"
            [isRowVisible]="configurationRowMatchesSearch"
            [header]="tableHeader"
            [columns]="tableColumns" />
        }
      }

      @if (operatorMappingsSetting(); as setting) {
        <aw-configuration-custom-input
          class="block w-full"
          [class.hidden]="!settingMatchesSearch(setting)"
          [setting]="setting"
          [inputId]="settingId(setting)"
          [control]="control(setting)" />
      }

      <ng-template #tableHeader>
        <div class="flex min-w-0 flex-1 items-center justify-start p-3">
          {{ cmsTextProvider.configuration.setting }}
        </div>
        <div
          class="flex min-w-0 items-center justify-start p-3"
          [ngClass]="{
            'flex-1': showWeb() && showApp(),
            'grow-2 basis-0': showWeb() && !showApp(),
            hidden: !showWeb(),
          }">
          <div class="flex items-center gap-2">
            <img
              class="size-5"
              src="images/amcon-webshop.svg"
              [alt]="cmsTextProvider.configuration.webLogoAlt" />
            {{ cmsTextProvider.license.web }}
          </div>
        </div>
        <div
          class="flex min-w-0 items-center justify-start p-3"
          [ngClass]="{
            'flex-1': showWeb() && showApp(),
            'grow-2 basis-0': showApp() && !showWeb(),
            hidden: !showApp(),
          }">
          <div class="flex items-center gap-2">
            <img
              class="size-5"
              src="images/amcon-app.svg"
              [alt]="cmsTextProvider.configuration.appLogoAlt" />
            {{ cmsTextProvider.license.app }}
          </div>
        </div>
        @if (!hasSingleActiveLicenseChannel()) {
          <div class="w-16 shrink-0" aria-hidden="true"></div>
        }
      </ng-template>

      <ng-template #tableColumns let-row="data">
        <aw-configuration-table-row
          class="contents"
          [row]="row"
          [showWeb]="showWeb()"
          [showApp]="showApp()"
          [hasSingleActiveLicenseChannel]="hasSingleActiveLicenseChannel()"
          [sharedControl]="row.shared ? control(row.shared) : undefined"
          [webControl]="row.web ? control(row.web) : undefined"
          [appControl]="row.app ? control(row.app) : undefined"
          [mergeMenuOpen]="mergeMenuSettingKey() === row.settingKey"
          (settingLinkToggled)="toggleSettingLink(row)"
          (mergeMenuClosed)="mergeMenuSettingKey.set(undefined)"
          (mergeChannelSelected)="selectMergeValue(row, $event)" />
      </ng-template>
    </div>
  }
</div>
